'use client';

import { useLocale, useTranslations } from 'next-intl';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent } from '@/app/components/ui/dialog';
import { Textarea } from '@/app/components/ui/textarea';
import { SITE } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import { getTodayDateString, reportEvent } from '@/app/utils';
import { safeStorage } from '@/app/utils/client';
import { useOpenPurchaseDialog } from '../_hook/use-open-purchase-dialog';
import { submitClawExpireRetainFeedback } from '../apis';

const headerImageZH =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/claw-expire-retain-dialog-header-zh-1e11d2eb.webp'
    : 'https://image01.cf.vidu.studio/vidu/media-asset/claw-expire-retain-dialog-header-zh-1e11d2eb.webp';

const headerImageEn =
  'https://image01.cf.vidu.studio/vidu/media-asset/claw-expire-retain-dialog-header-en-5c8d745e.webp';

const MAX_FEEDBACK_LENGTH = 200;

// 试用挽留弹窗：试用到期前 71h 内、每自然日最多 1 次、同一 tab 仅 1 次
const TRIGGER_WINDOW_MS = 71 * 60 * 60 * 1000;
const DAILY_LIMIT = 1;
const DAILY_KEY = 'CLAW_EXPIRE_RETAIN_DIALOG_DAILY';
const TAB_SHOWN_KEY = 'CLAW_EXPIRE_RETAIN_DIALOG_TAB_SHOWN';

interface DailyRecord {
  date: string;
  count: number;
}

const readDailyRecord = (): DailyRecord => {
  const raw = safeStorage.getItem(DAILY_KEY);
  if (!raw) return { date: '', count: 0 };
  try {
    const parsed = JSON.parse(raw) as Partial<DailyRecord>;
    return {
      date: typeof parsed.date === 'string' ? parsed.date : '',
      count: typeof parsed.count === 'number' ? parsed.count : 0,
    };
  } catch {
    return { date: '', count: 0 };
  }
};

const isTabAlreadyShown = (): boolean => {
  if (typeof window === 'undefined') return true;
  try {
    return window.sessionStorage.getItem(TAB_SHOWN_KEY) === '1';
  } catch {
    return true;
  }
};

const markTabShown = () => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(TAB_SHOWN_KEY, '1');
  } catch {
    // sessionStorage 不可用时忽略：丢失"单 tab 一次"约束总比阻塞渲染好
  }
};

const incrementDailyCount = () => {
  const today = getTodayDateString();
  const record = readDailyRecord();
  const next: DailyRecord =
    record.date === today ? { date: today, count: record.count + 1 } : { date: today, count: 1 };
  safeStorage.setItem(DAILY_KEY, JSON.stringify(next));
};

const ComputeIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width="1em" height="1em" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      d="M12.8305 2.18036C12.5699 2.06151 12.2868 2 12.0005 2C11.7141 2 11.431 2.06151 11.1705 2.18036L2.60045 6.08036C2.423 6.1586 2.27213 6.28676 2.16621 6.44921C2.0603 6.61167 2.00391 6.80142 2.00391 6.99536C2.00391 7.18929 2.0603 7.37904 2.16621 7.5415C2.27213 7.70396 2.423 7.83211 2.60045 7.91036L11.1805 11.8204C11.441 11.9391 11.7241 12.0005 12.0105 12.0004C12.2968 12.0005 12.5799 11.9391 12.8405 11.8204L21.4205 7.92036C21.5982 7.84225 21.7494 7.71409 21.8555 7.55152C21.9616 7.38895 22.0181 7.199 22.0181 7.00486C22.0181 6.81071 21.9616 6.62076 21.8555 6.45819C21.7494 6.29562 21.5982 6.16747 21.4205 6.08936L12.8305 2.18036Z"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
    <path d="M16 17H22" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
    <path d="M19 14V20" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
    <path
      d="M2 12C1.99953 12.1913 2.05392 12.3787 2.15672 12.5399C2.25952 12.7012 2.40642 12.8297 2.58 12.91L11.18 16.82C11.4392 16.9373 11.7205 16.998 12.005 16.998"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
    <path
      d="M2 17C1.99953 17.1913 2.05392 17.3787 2.15672 17.5399C2.25952 17.7012 2.40642 17.8297 2.58 17.91L11.18 21.82C11.4392 21.9374 11.7205 21.9981 12.005 21.9981C12.2895 21.9981 12.5708 21.9374 12.83 21.82L14.946 20.858"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
);

const AgentSparkIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width="1em" height="1em" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      d="M12.9815 21.1864C12.9386 21.4158 12.8169 21.623 12.6374 21.7721C12.4579 21.9212 12.2319 22.0028 11.9985 22.0028C11.7651 22.0028 11.5391 21.9212 11.3596 21.7721C11.1801 21.623 11.0583 21.4158 11.0155 21.1864C10.6707 19.136 9.69527 17.2441 8.22505 15.7739C6.75483 14.3037 4.8629 13.3283 2.81249 12.9834C2.58309 12.9406 2.3759 12.8189 2.22681 12.6393C2.07771 12.4598 1.99609 12.2338 1.99609 12.0004C1.99609 11.7671 2.07771 11.5411 2.22681 11.3615C2.3759 11.182 2.58309 11.0603 2.81249 11.0174C4.8629 10.6726 6.75483 9.69722 8.22505 8.227C9.69527 6.75678 10.6707 4.86485 11.0155 2.81444C11.0583 2.58504 11.1801 2.37786 11.3596 2.22876C11.5391 2.07966 11.7651 1.99805 11.9985 1.99805C12.2319 1.99805 12.4579 2.07966 12.6374 2.22876C12.8169 2.37786 12.9386 2.58504 12.9815 2.81444C13.3263 4.86485 14.3017 6.75678 15.7719 8.227C17.2421 9.69722 19.1341 10.6726 21.1845 11.0174C21.4139 11.0603 21.6211 11.182 21.7702 11.3615C21.9193 11.5411 22.0009 11.7671 22.0009 12.0004C22.0009 12.2338 21.9193 12.4598 21.7702 12.6393C21.6211 12.8189 21.4139 12.9406 21.1845 12.9834C19.1341 13.3283 17.2421 14.3037 15.7719 15.7739C14.3017 17.2441 13.3263 19.136 12.9815 21.1864Z"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
);

const PercentBadgeIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width="1em" height="1em" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      d="M3.84922 8.61912C3.70326 7.96165 3.72567 7.27796 3.91437 6.63146C4.10308 5.98496 4.45196 5.39657 4.92868 4.92084C5.40541 4.44512 5.99453 4.09747 6.64142 3.91012C7.28832 3.72277 7.97205 3.70179 8.62922 3.84912C8.99093 3.28342 9.48922 2.81788 10.0782 2.49541C10.6671 2.17293 11.3278 2.00391 11.9992 2.00391C12.6707 2.00391 13.3313 2.17293 13.9203 2.49541C14.5092 2.81788 15.0075 3.28342 15.3692 3.84912C16.0274 3.70114 16.7123 3.72203 17.3602 3.90983C18.0081 4.09764 18.598 4.44626 19.0751 4.92327C19.5521 5.40029 19.9007 5.99019 20.0885 6.63812C20.2763 7.28605 20.2972 7.97095 20.1492 8.62912C20.7149 8.99083 21.1805 9.48912 21.5029 10.0781C21.8254 10.667 21.9944 11.3277 21.9944 11.9991C21.9944 12.6706 21.8254 13.3312 21.5029 13.9202C21.1805 14.5091 20.7149 15.0074 20.1492 15.3691C20.2966 16.0263 20.2756 16.71 20.0882 17.3569C19.9009 18.0038 19.5532 18.5929 19.0775 19.0697C18.6018 19.5464 18.0134 19.8953 17.3669 20.084C16.7204 20.2727 16.0367 20.2951 15.3792 20.1491C15.018 20.717 14.5193 21.1845 13.9293 21.5084C13.3394 21.8324 12.6772 22.0022 12.0042 22.0022C11.3312 22.0022 10.669 21.8324 10.0791 21.5084C9.48914 21.1845 8.99045 20.717 8.62922 20.1491C7.97205 20.2965 7.28832 20.2755 6.64142 20.0881C5.99453 19.9008 5.40541 19.5531 4.92868 19.0774C4.45196 18.6017 4.10308 18.0133 3.91437 17.3668C3.72567 16.7203 3.70326 16.0366 3.84922 15.3791C3.27917 15.0184 2.80963 14.5193 2.48426 13.9283C2.1589 13.3374 1.98828 12.6737 1.98828 11.9991C1.98828 11.3245 2.1589 10.6609 2.48426 10.0699C2.80963 9.47895 3.27917 8.97988 3.84922 8.61912Z"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
    <path d="M15 9L9 15" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
    <path d="M9 9H9.01" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
    <path d="M15 15H15.01" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
  </svg>
);

interface FeatureItem {
  icon: React.ReactNode;
  titleKey: 'feature1Title' | 'feature2Title' | 'feature3Title';
  descKey: 'feature1Description' | 'feature2Description' | 'feature3Description';
}

const FEATURE_ITEMS: FeatureItem[] = [
  {
    icon: <ComputeIcon className="size-6" />,
    titleKey: 'feature1Title',
    descKey: 'feature1Description',
  },
  {
    icon: <AgentSparkIcon className="size-6" />,
    titleKey: 'feature2Title',
    descKey: 'feature2Description',
  },
  {
    icon: <PercentBadgeIcon className="size-6" />,
    titleKey: 'feature3Title',
    descKey: 'feature3Description',
  },
];

export const ExpireRetainDialog = () => {
  const t = useTranslations();
  const locale = useLocale();
  const { clawPass, isClawInfoInitialized } = useStore((state) => ({
    clawPass: state.clawPass,
    isClawInfoInitialized: state.isClawInfoInitialized,
  }));
  const openPurchaseDialog = useOpenPurchaseDialog();

  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  // 触发判断每个组件实例只跑一次，避免 clawPass 后续刷新时被重新评估又弹一次
  const hasEvaluatedRef = useRef(false);

  // 触发条件：clawPass 加载完成、试用用户、距到期 < 71h，
  // 同时受 sessionStorage（同 tab 一次）和 localStorage（自然日一次）限制
  useEffect(() => {
    if (hasEvaluatedRef.current) return;
    if (!isClawInfoInitialized || !clawPass) return;
    if (clawPass.userVariant !== 'trial') return;
    if (!clawPass.valid_to) return;

    const remainingMs = new Date(clawPass.valid_to).getTime() - Date.now();
    if (Number.isNaN(remainingMs) || remainingMs <= 0 || remainingMs >= TRIGGER_WINDOW_MS) return;

    if (isTabAlreadyShown()) {
      hasEvaluatedRef.current = true;
      return;
    }

    const today = getTodayDateString();
    const record = readDailyRecord();
    if (record.date === today && record.count >= DAILY_LIMIT) {
      hasEvaluatedRef.current = true;
      return;
    }

    hasEvaluatedRef.current = true;
    markTabShown();
    incrementDailyCount();
    reportEvent('claw_trial_expire_retain_dialog_exposure', {
      remaining_hours: Math.floor(remainingMs / (60 * 60 * 1000)),
    });
    setOpen(true);
  }, [isClawInfoInitialized, clawPass]);

  // 关闭/购买时若有输入则 fire-and-forget 提交，不阻塞用户操作
  const submitIfNeeded = () => {
    const content = text.trim();
    if (!content) return;
    submitClawExpireRetainFeedback(content).catch(() => {});
  };

  const handleClose = () => {
    submitIfNeeded();
    setText('');
    setOpen(false);
  };

  const handlePurchase = () => {
    submitIfNeeded();
    setText('');
    setOpen(false);
    // 用试用挽留独立来源打开购买弹窗，便于区分埋点；
    // ClawPassContentDialog 已对该来源开启挽留分支：free 用户没点订阅就关闭时会触发 ClawRetentionDialog
    openPurchaseDialog('claw_page_trial_expire_retain');
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) handleClose();
      }}
    >
      <DialogContent
        className="flex w-full max-w-[580px] flex-col items-stretch gap-0 overflow-hidden rounded-16 border border-system-white12 bg-[#121316] p-0 shadow-[0px_20px_60px_0px_rgba(0,0,0,0.32)] sm:max-w-[calc(100%-32px)] sm:gap-0 sm:p-0 md:p-0"
        closeClassName="top-4 right-4 size-6 text-white/80 hover:text-white"
      >
        <div className="relative w-full bg-[#191D25]">
          <img
            src={locale === 'zh' ? headerImageZH : headerImageEn}
            alt=""
            className="block aspect-[580/326] w-full object-cover"
            draggable={false}
          />
        </div>

        <div className="-mt-10 relative z-10 flex flex-col items-center gap-8 px-6 pb-10">
          <div className="flex w-full flex-col items-center gap-3">
            <p
              className="text-center font-semibold text-base text-white sm:text-lg"
              style={locale === 'zh' ? { letterSpacing: '10.8px' } : undefined}
            >
              {t('claw.expireRetainDialog.title')}
            </p>

            <div className="flex w-full items-stretch py-5">
              {FEATURE_ITEMS.map((item, index) => (
                <Fragment key={item.titleKey}>
                  {index > 0 && <div className="mx-2 w-px self-stretch bg-system-white12" />}
                  <div className="flex min-w-0 flex-1 flex-col items-center justify-start gap-1 text-center text-white">
                    <div className="flex size-9 items-center justify-center">{item.icon}</div>
                    <p className="font-semibold text-sm">{t(`claw.expireRetainDialog.${item.titleKey}`)}</p>
                    <p className="whitespace-pre-line text-system-white36 text-xs leading-snug">
                      {t(`claw.expireRetainDialog.${item.descKey}`)}
                    </p>
                  </div>
                </Fragment>
              ))}
            </div>

            <div className="w-full px-4">
              <Textarea
                className="h-[88px] resize-none rounded-16 border-none bg-system-white05 px-4 pt-3 pb-12 text-sm text-system-white placeholder:text-system-white36 focus-visible:ring-0 focus-visible:ring-offset-0"
                placeholder={t('claw.expireRetainDialog.placeholder')}
                maxLength={MAX_FEEDBACK_LENGTH}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
            </div>
          </div>

          <Button
            variant="primary"
            className="h-12 w-full max-w-[316px] rounded-12 bg-white font-semibold text-black text-sm transition-colors hover:bg-white/90"
            onClick={handlePurchase}
          >
            {t('claw.expireRetainDialog.button')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
